/**
 * CommsPro — Hooks and micro-components that upgrade the Communication Center
 * with priority/tags/assignee, @mentions, typing indicators, read-receipts,
 * templates, and real-time polling.
 *
 * Keeps CommunicationsPage.jsx lean — everything PRO lives here.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Users, ChatCircleDots, Lightning, Warning, Tag, Check, Checks, Plus, Trash, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

// ═════════════════════════════════════════════════════════════════════════════
// Hooks
// ═════════════════════════════════════════════════════════════════════════════

/** Poll typing status every 3s while on a thread; send heartbeat while user is typing. */
export function useTypingIndicator(threadId, myEmail) {
  const [typers, setTypers] = useState([]);
  const heartbeatRef = useRef(null);

  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/threads/${threadId}/typing`);
        if (!cancelled) setTypers(data.typers || []);
      } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[comms] typers poll failed:", e?.message); }
    };
    load();
    const iv = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(iv); setTypers([]); };
  }, [threadId]);

  const notifyTyping = useCallback(() => {
    if (!threadId) return;
    if (heartbeatRef.current) return; // throttle: one heartbeat per 3s
    heartbeatRef.current = setTimeout(() => { heartbeatRef.current = null; }, 3000);
    api.post(`/threads/${threadId}/typing`).catch(() => {});
  }, [threadId]);

  return { typers, notifyTyping };
}

/** Global polling for unread counts + thread updates. Runs every 15s. */
export function useCommsPolling(onUpdate) {
  const sinceRef = useRef(null);
  const [totals, setTotals] = useState({ total_unread: 0, mentions_unread: 0 });

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const params = sinceRef.current ? { since: sinceRef.current } : {};
        const { data } = await api.get("/threads/updates/poll", { params });
        if (cancelled) return;
        sinceRef.current = data.server_time;
        setTotals({ total_unread: data.total_unread || 0, mentions_unread: data.mentions_unread || 0 });
        if (onUpdate && data.changed_threads?.length) onUpdate(data.changed_threads);
      } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[comms] updates poll failed:", e?.message); }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [onUpdate]);

  return totals;
}

/** Fetch mentionable users once (used by @autocomplete and assignee picker). */
export function useMentionableUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/threads/directory/mentionable").then(r => setUsers(r.data.users || [])).catch(() => {});
  }, []);
  return users;
}

// ═════════════════════════════════════════════════════════════════════════════
// Components
// ═════════════════════════════════════════════════════════════════════════════

const PRIORITY_STYLES = {
  high:   { chip: "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800", icon: Warning, label: "Alta" },
  medium: { chip: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800", icon: Lightning, label: "Media" },
  low:    { chip: "bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700", icon: ChatCircleDots, label: "Baja" },
};

export function PriorityChip({ priority = "medium", size = 11 }) {
  const cfg = PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wider ${cfg.chip}`}>
      <Icon size={size} weight="fill" /> {cfg.label}
    </span>
  );
}

export function TagChip({ tag, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[10px] font-medium">
      <Tag size={9} /> {tag}
      {onRemove && (
        <button onClick={onRemove} className="hover:text-rose-500 ml-0.5" aria-label={`Remove ${tag}`}>×</button>
      )}
    </span>
  );
}

/** Priority + tags + assignee inline editor for the thread header. */
export function ThreadMetaPanel({ thread, onPatched, users }) {
  const [priority, setPriority] = useState(thread?.priority || "medium");
  const [tags, setTags] = useState(thread?.tags || []);
  const [assignee, setAssignee] = useState(thread?.assignee_email || "");
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPriority(thread?.priority || "medium");
    setTags(thread?.tags || []);
    setAssignee(thread?.assignee_email || "");
  }, [thread?.id, thread?.priority, thread?.tags, thread?.assignee_email]);

  const patch = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/threads/${thread.id}`, payload);
      onPatched?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const changePriority = (p) => { setPriority(p); patch({ priority: p }); };
  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || tags.includes(t) || tags.length >= 8) { setTagInput(""); return; }
    const next = [...tags, t];
    setTags(next);
    setTagInput("");
    patch({ tags: next });
  };
  const removeTag = (t) => {
    const next = tags.filter(x => x !== t);
    setTags(next);
    patch({ tags: next });
  };
  const changeAssignee = (v) => { setAssignee(v); patch({ assignee_email: v }); };

  const assigneeUser = users.find(u => u.email === assignee);

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 text-xs">
      {/* Priority */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Prioridad:</span>
        {["high", "medium", "low"].map(p => (
          <button
            key={p}
            onClick={() => changePriority(p)}
            disabled={busy}
            className={`px-1.5 py-0.5 rounded border text-[10px] transition-all ${
              priority === p
                ? PRIORITY_STYLES[p].chip
                : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"
            }`}
            data-testid={`priority-${p}`}
          >
            {PRIORITY_STYLES[p].label}
          </button>
        ))}
      </div>

      <span className="text-slate-300 dark:text-slate-700">|</span>

      {/* Assignee */}
      <div className="flex items-center gap-1">
        <Users size={12} className="text-slate-500" />
        <select
          value={assignee}
          onChange={(e) => changeAssignee(e.target.value)}
          disabled={busy}
          className="text-xs bg-transparent border-0 focus:outline-none focus:ring-0 text-slate-700 dark:text-slate-200 cursor-pointer"
          data-testid="assignee-select"
        >
          <option value="">Sin asignar</option>
          {users.map(u => (
            <option key={u.email} value={u.email}>
              {u.name || u.email} · {u.role}
            </option>
          ))}
        </select>
        {assigneeUser && (
          <span className="text-[10px] text-slate-400">→ {assigneeUser.email}</span>
        )}
      </div>

      <span className="text-slate-300 dark:text-slate-700">|</span>

      {/* Tags */}
      <div className="flex items-center gap-1 flex-wrap">
        <Tag size={12} className="text-slate-500" />
        {tags.map(t => <TagChip key={t} tag={t} onRemove={() => removeTag(t)} />)}
        <input
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
          placeholder="+ etiqueta"
          disabled={busy || tags.length >= 8}
          className="text-[11px] px-1 py-0.5 rounded bg-transparent border border-slate-200 dark:border-slate-700 focus:outline-none focus:border-amber-400 text-slate-700 dark:text-slate-200 w-20"
          data-testid="tag-input"
        />
      </div>
    </div>
  );
}

/** Dropdown of typers currently typing in the thread. */
export function TypingDots({ typers }) {
  if (!typers || typers.length === 0) return null;
  const names = typers.map(t => t.name || (t.email || "").split("@")[0]).join(", ");
  return (
    <div className="px-4 py-1.5 text-[11px] text-slate-500 dark:text-slate-400 italic flex items-center gap-2">
      <span className="inline-flex gap-0.5">
        <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
      </span>
      {names} escribiendo...
    </div>
  );
}

/** Templates dropdown for the composer. */
export function TemplatesMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", shortcut: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/threads/templates/list");
      setItems(data.templates || []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[comms] template list failed:", e?.message); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Título y contenido requeridos"); return; }
    try {
      await api.post("/threads/templates/list", form);
      toast.success("Plantilla creada");
      setForm({ title: "", body: "", shortcut: "" });
      setCreating(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!confirm("¿Borrar esta plantilla?")) return;
    try {
      await api.delete(`/threads/templates/list/${id}`);
      load();
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[comms] template delete failed:", e?.message); }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-900 dark:hover:text-white"
        title="Plantillas / respuestas rápidas"
        data-testid="templates-button"
      >
        <ChatCircleDots size={16} weight="regular" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full mb-2 left-0 w-80 max-h-[420px] overflow-y-auto z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wider">Plantillas</div>
              <button
                onClick={() => setCreating(v => !v)}
                className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
              >
                <Plus size={12} /> Nueva
              </button>
            </div>

            {creating && (
              <div className="p-3 border-b border-slate-200 dark:border-slate-800 space-y-2 bg-slate-50 dark:bg-slate-900/60">
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Título (ej. Factura enviada)"
                  className="w-full text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                  data-testid="tpl-title"
                />
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Contenido..."
                  rows={3}
                  className="w-full text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 resize-none"
                  data-testid="tpl-body"
                />
                <input
                  value={form.shortcut}
                  onChange={(e) => setForm({ ...form, shortcut: e.target.value })}
                  placeholder="Atajo opcional (ej. /factura)"
                  className="w-full text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                />
                <div className="flex gap-2">
                  <button onClick={create} className="flex-1 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded px-2 py-1" data-testid="tpl-save">Guardar</button>
                  <button onClick={() => setCreating(false)} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 px-2 py-1">Cancelar</button>
                </div>
              </div>
            )}

            {items.length === 0 && !creating && (
              <div className="px-3 py-6 text-center text-xs text-slate-500 dark:text-slate-400">
                No hay plantillas. Crea una para reutilizar respuestas frecuentes.
              </div>
            )}

            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(tpl => (
                <div
                  key={tpl.id}
                  className="px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer group flex items-start justify-between gap-2"
                  onClick={() => { onPick?.(tpl.body); setOpen(false); }}
                  data-testid={`tpl-item-${tpl.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2">
                      {tpl.title}
                      {tpl.shortcut && <span className="text-[10px] font-mono text-amber-600 dark:text-amber-400">{tpl.shortcut}</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{tpl.body}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(tpl.id); }}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500"
                    aria-label="Delete"
                  >
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** @-mention autocomplete overlay. Detects "@..." at cursor and shows dropdown. */
export function MentionAutocomplete({ textareaRef, value, onChange, users }) {
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [selectedIdx, setSelectedIdx] = useState(0);

  const detect = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const caret = ta.selectionStart;
    const before = value.slice(0, caret);
    // Find last @ that is not preceded by a word char
    const m = before.match(/(?:^|\s)@([\w\.\-]*)$/);
    if (!m) { setVisible(false); return; }
    setQuery(m[1].toLowerCase());
    setSelectedIdx(0);
    setVisible(true);
    // rudimentary positioning: place below the textarea top-left corner
    const rect = ta.getBoundingClientRect();
    setPos({ top: rect.top - 8, left: rect.left + 40 });
  }, [textareaRef, value]);

  useEffect(() => { detect(); }, [value, detect]);

  const filtered = users
    .filter(u => {
      const q = query;
      if (!q) return true;
      return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
    })
    .slice(0, 6);

  const pick = (u) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const caret = ta.selectionStart;
    const before = value.slice(0, caret);
    const after = value.slice(caret);
    const newBefore = before.replace(/@([\w\.\-]*)$/, `@${u.email} `);
    const next = newBefore + after;
    onChange(next);
    setVisible(false);
    setTimeout(() => {
      ta.focus();
      const newCaret = newBefore.length;
      ta.setSelectionRange(newCaret, newCaret);
    }, 0);
  };

  const handleKey = useCallback((e) => {
    if (!visible) return false;
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx((i) => Math.min(i + 1, filtered.length - 1)); return true; }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx((i) => Math.max(i - 1, 0)); return true; }
    if (e.key === "Enter" || e.key === "Tab") {
      if (filtered[selectedIdx]) { e.preventDefault(); pick(filtered[selectedIdx]); return true; }
    }
    if (e.key === "Escape") { e.preventDefault(); setVisible(false); return true; }
    return false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, filtered, selectedIdx]);

  // expose the key handler
  MentionAutocomplete.handleKeyStatic = handleKey;

  if (!visible || filtered.length === 0) return null;

  return (
    <div
      className="fixed z-[70] w-64 max-h-56 overflow-y-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl"
      style={{ top: pos.top - 220, left: pos.left }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200 dark:border-slate-800">
        Mencionar a
      </div>
      {filtered.map((u, i) => (
        <div
          key={u.email}
          onClick={() => pick(u)}
          className={`px-3 py-2 cursor-pointer text-xs ${i === selectedIdx ? "bg-amber-50 dark:bg-amber-950/30" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}
        >
          <div className="font-medium text-slate-800 dark:text-slate-200">{u.name || u.email}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">{u.email} · {u.role}</div>
        </div>
      ))}
    </div>
  );
}

/** Small "Seen by X" line under own messages. Loads read-by lazily on hover. */
export function ReadReceipt({ message, myEmail }) {
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);

  if (message.sender_email !== myEmail) return null;

  const readers = (message.read_by || []).filter(e => e !== myEmail);
  if (readers.length === 0) {
    return (
      <span className="inline-flex items-center text-[10px] text-slate-400 gap-0.5">
        <Check size={10} />
      </span>
    );
  }

  const load = async () => {
    if (detail) return;
    try {
      const { data } = await api.get(`/threads/messages/${message.id}/read-by`);
      setDetail(data.read_by || []);
    } catch { setDetail([]); }
  };

  return (
    <span
      className="inline-flex items-center text-[10px] text-emerald-600 dark:text-emerald-400 gap-0.5 cursor-help relative"
      onMouseEnter={() => { setOpen(true); load(); }}
      onMouseLeave={() => setOpen(false)}
    >
      <Checks size={10} weight="bold" /> {readers.length}
      {open && detail && detail.length > 0 && (
        <span className="absolute bottom-full right-0 mb-1 whitespace-nowrap bg-slate-900 dark:bg-slate-800 text-white text-[10px] px-2 py-1 rounded shadow-lg z-50">
          Leído por {detail.map(u => u.name || u.email).join(", ")}
        </span>
      )}
    </span>
  );
}

/** Renders message body with @mentions highlighted. */
export function MessageBody({ body, myEmail }) {
  const parts = [];
  const regex = /@([\w\.\-]+@[\w\.\-]+\.[\w]+|[\w\.\-]+)/g;
  let last = 0;
  let m;
  while ((m = regex.exec(body)) !== null) {
    if (m.index > last) parts.push({ text: body.slice(last, m.index), mention: false });
    const target = m[1];
    const isMe = myEmail && (target.toLowerCase() === myEmail.toLowerCase() || myEmail.toLowerCase().startsWith(target.toLowerCase()));
    parts.push({ text: "@" + target, mention: true, isMe });
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push({ text: body.slice(last), mention: false });

  return (
    <>
      {parts.map((p, i) =>
        p.mention ? (
          <span
            key={i}
            className={`font-medium px-1 py-0.5 rounded ${
              p.isMe
                ? "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
                : "text-amber-700 dark:text-amber-400"
            }`}
          >
            {p.text}
          </span>
        ) : (
          <span key={i} style={{ whiteSpace: "pre-wrap" }}>{p.text}</span>
        )
      )}
    </>
  );
}
